import { ReactNode, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { CircularProgress, Box, Typography, Button } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { colors } from "../../assets/styles/tokens";
import {
  type Workspace,
  selectSelectedWorkspace,
  selectAvailableWorkspaces,
  selectAllApiWebsites,
  selectIsLoadingAllWebsites,
  selectWorkspaceLoading,
  initializeWorkspaces,
  initializeWorkspaceData,
  setSelectedWorkspace,
  setAllApiWebsites,
  setIsLoadingAllWebsites,
  clearSelectedWorkspace,
  resetWorkspaceState,
} from "../../features/workspaceSlice";
import { loginService } from "../../services/login.service";
import { selectUserData, selectUserToken, setDisplayErrorSnackBar } from "../../features/userSlice";
import { useDeviceDetection } from "../../customHooks/useDeviceDetection";
import { websitesActionsService } from "../../services/api/websites-actions.endpoints";
import {
  transformApiWebsitesToWorkspaces,
  hasOrgSummaryPermission,
} from "../../utils/workspaceUtils";

interface WorkspaceGuardProps {
  children: ReactNode;
}

export const WorkspaceGuard = ({ children }: WorkspaceGuardProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { deviceType, isDetecting } = useDeviceDetection();
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await loginService.logout();
      dispatch(clearSelectedWorkspace());
      dispatch(resetWorkspaceState());
      searchParams.delete("workspace");
      setSearchParams(searchParams, { replace: true });
      navigate("/logout");
    } catch (error) {
      dispatch(setDisplayErrorSnackBar({ message: "Logout failed. Please try again.", type: "error" }));
    } finally {
      setIsLoggingOut(false);
    }
  };

  const selectedWorkspace = useSelector(selectSelectedWorkspace);
  const availableWorkspaces = useSelector(selectAvailableWorkspaces);
  const allApiWebsites = useSelector(selectAllApiWebsites);
  const isLoadingAllWebsites = useSelector(selectIsLoadingAllWebsites);
  const isLoading = useSelector(selectWorkspaceLoading);
  const userData = useSelector(selectUserData);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const canSeeAllWebsites = hasOrgSummaryPermission(userData?.data?.products);
  const isFetchingAllWebsites = useRef(false);

  // Environment detection
  const environment = import.meta.env.VITE_ENV;
  const isProduction = environment === "production";
  const isStaging = environment === "staging" || environment === "development";
  const isDesktop = deviceType === "desktop";

  // Determine if workspace should be disabled
  const shouldDisableWorkspace =
    (isProduction && isDesktop) || (isStaging && isDesktop);

  // Get workspace UUID from URL
  const workspaceIdFromUrl = searchParams.get("workspace");

  // EFFECT 1: Initialize workspaces from user data (only once)
  useEffect(() => {
    if (userData?.data && availableWorkspaces.length === 0) {
      dispatch(initializeWorkspaces(userData.data));
      dispatch(initializeWorkspaceData(userData.data));
    }
  }, [userData, availableWorkspaces.length, dispatch]);

  // EFFECT: Eagerly fetch all websites on mobile after workspace init.
  // Runs once workspaces are ready and device detection has settled.
  // Desktop has no use case for this yet — skipped via isDesktop guard.
  useEffect(() => {
    if (isDesktop || !canSeeAllWebsites || !oauthTokenHeader) return;
    if (availableWorkspaces.length === 0) return;
    if (allApiWebsites.length > 0 || isFetchingAllWebsites.current) return;

    isFetchingAllWebsites.current = true;
    dispatch(setIsLoadingAllWebsites(true));
    websitesActionsService
      .getAllWebsiteList(oauthTokenHeader)
      .then((response) => {
        if (response?.status === 200) {
          const activeWorkspaces = transformApiWebsitesToWorkspaces(response.data.data);
          dispatch(setAllApiWebsites(activeWorkspaces));
        }
      })
      .catch(() => {})
      .finally(() => {
        isFetchingAllWebsites.current = false;
        dispatch(setIsLoadingAllWebsites(false));
      });
  }, [isDesktop, canSeeAllWebsites, oauthTokenHeader, availableWorkspaces.length, allApiWebsites.length, dispatch]);

  // EFFECT 2: Handle workspace selection logic
  useEffect(() => {
    // Wait for workspaces to load
    if (availableWorkspaces.length === 0) {
      // Only stop loading if the user genuinely has no website_permissions.
      // During logout, availableWorkspaces resets to [] but website_permissions
      // still has values — don't treat that as a no-permission error.
      if (userData?.data && !userData.data.website_permissions?.length) {
        setIsInitializing(false);
      }
      return;
    }

    // If workspace is disabled, remove query param and select first workspace silently
    if (shouldDisableWorkspace) {
      // Remove workspace from URL if it exists
      if (workspaceIdFromUrl) {
        searchParams.delete("workspace");
        setSearchParams(searchParams, { replace: true });
      }

      // Select first workspace if none selected (but don't add to URL)
      if (!selectedWorkspace) {
        const firstWorkspace = availableWorkspaces[0];
        dispatch(setSelectedWorkspace(firstWorkspace));
      }

      // Mark initialization as complete
      setIsInitializing(false);
      return;
    }

    // Normal workspace behavior when not disabled
    // Case 1: Workspace ID exists in URL
    if (workspaceIdFromUrl) {
      // Multi-site "All Websites" case: comma-separated list of UIDs.
      // IDs may come from /asset_management/websites/ (not just /me),
      // so we skip per-ID validation and trust the param as-is.
      const isMultiSite = workspaceIdFromUrl.includes(",");
      if (isMultiSite) {
        setIsInitializing(false);
        return;
      }

      const workspaceFromUrl = availableWorkspaces.find(
        (w: any) => w.id === workspaceIdFromUrl,
      );

      if (workspaceFromUrl) {
        // Valid workspace in URL - select it if not already selected
        if (selectedWorkspace?.id !== workspaceFromUrl.id) {
          dispatch(setSelectedWorkspace(workspaceFromUrl));
        }
      } else {
        // Invalid workspace ID in URL - remove it and select first
        searchParams.delete("workspace");
        setSearchParams(searchParams, { replace: true });

        if (!selectedWorkspace) {
          const firstWorkspace = availableWorkspaces[0];
          dispatch(setSelectedWorkspace(firstWorkspace));
          searchParams.set("workspace", firstWorkspace.id);
          setSearchParams(searchParams, { replace: true });
        }
      }

      // Mark initialization as complete
      setIsInitializing(false);
      return;
    }

    // Case 2: No workspace in URL and no workspace selected
    if (!selectedWorkspace) {
      const firstWorkspace = availableWorkspaces[0];
      dispatch(setSelectedWorkspace(firstWorkspace));
      searchParams.set("workspace", firstWorkspace.id);
      setSearchParams(searchParams, { replace: true });

      // Mark initialization as complete
      setIsInitializing(false);
      return;
    }

    // Case 3: Workspace is selected but not in URL
    if (selectedWorkspace && !workspaceIdFromUrl) {
      searchParams.set("workspace", selectedWorkspace.id);
      setSearchParams(searchParams, { replace: true });
    }

    // Mark initialization as complete
    setIsInitializing(false);
  }, [
    availableWorkspaces,
    selectedWorkspace,
    workspaceIdFromUrl,
    shouldDisableWorkspace,
    userData,
    dispatch,
    searchParams,
    setSearchParams,
  ]);

  // EFFECT 3: Sync the URL after allApiWebsites is populated (by EFFECT 1) when in
  // multi-site mode — removes any stale/deactivated IDs that persisted from a prior session.
  useEffect(() => {
    const isMultiSiteUrl = workspaceIdFromUrl?.includes(",");
    if (!isMultiSiteUrl || !canSeeAllWebsites || allApiWebsites.length === 0) return;

    const freshIds = allApiWebsites.map((w: Workspace) => w.id).join(",");
    if (freshIds && freshIds !== workspaceIdFromUrl) {
      setSearchParams((prev) => {
        const updated = new URLSearchParams(prev);
        updated.set("workspace", freshIds);
        return updated;
      }, { replace: true });
    }
  }, [workspaceIdFromUrl, canSeeAllWebsites, allApiWebsites, setSearchParams]);

  // Show loading while user data is loading, workspace is loading, device is detecting, or initializing
  if (!userData?.data || isLoading || isDetecting || isInitializing || isLoadingAllWebsites) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
          gap: 3,
        }}
      >
        <CircularProgress size={60} thickness={4} color="secondary" />
        <Typography variant="h6" color="textSecondary">
          Initializing Aladdin...
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Please wait
        </Typography>
      </Box>
    );
  }

  // No workspaces available (only show this after initialization is complete)
  // Guard against the logout transition where availableWorkspaces resets to []
  // but the user still has website_permissions in their data.
  if (availableWorkspaces.length === 0 && !userData?.data?.website_permissions?.length) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          bgcolor: "background.default",
          color: "text.primary",
          textAlign: "center",
          gap: 2,
          p: 3,
        }}
      >
        <Typography
          variant="h1"
          component="h1"
          sx={{ fontWeight: 700, color: colors.Brand[700].$value }}
        >
          401
        </Typography>
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{ color: colors.GrayLightMode[900].$value }}
        >
          You need access to continue
        </Typography>
        <Typography
          variant="body1"
          sx={{ maxWidth: 420, color: colors.GrayLightMode[500].$value }}
        >
          Looks like your account doesn't have website permissions yet. No
          worries, just reach out to your administrator and they'll get you set
          up quickly.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
            sx={{
              borderColor: colors.Brand[700].$value,
              color: colors.Brand[700].$value,
            }}
          >
            Retry
          </Button>
          <Button
            variant="contained"
            onClick={handleLogout}
            disabled={isLoggingOut}
            startIcon={!isLoggingOut && <LogoutIcon />}
            sx={{
              bgcolor: colors.Brand[700].$value,
              "&:hover": { bgcolor: colors.Brand[800].$value },
              minWidth: 100,
            }}
          >
            {isLoggingOut ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Logout"
            )}
          </Button>
        </Box>
      </Box>
    );
  }

  // Don't render children until workspace is selected
  // Exception: multi-site mode has no single selectedWorkspace — the URL is the selection
  const isMultiSiteMode = workspaceIdFromUrl?.includes(",") ?? false;
  if (!selectedWorkspace && !isMultiSiteMode) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="textSecondary">
          Selecting workspace...
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};
